
import api from './api';

// ============ Types ============

export interface SocialProfile {
    id: number;
    platform: string;
    platform_display: string;
    handle: string;
    url: string;
    avatar_url?: string;
    follower_count: number;
    avg_engagement_rate: number;
    avg_video_views: number;
    bio: string;
    location?: string;
    external_links?: string[];
    video_count?: number;
    total_views?: number;
    join_date?: string;
    updated_at: string;
    email?: string;
}

export interface InfluencerAnalysis {
    id: number;
    brand_fit_score: number;
    commercial_value: string;
    growth_potential: string;
    audience_persona: any;
    content_style: string;
    platform_strengths: string;
    summary: string;
    created_at: string;
}

export interface Influencer {
    id: number;
    name: string;
    avatar?: string;
    category: string;
    tags: string[];
    status: 'new' | 'pending' | 'contacted' | 'cooperating' | 'rejected';
    status_display: string;
    contact_info: string;
    email?: string;
    notes: string;
    created_at: string;
    updated_at: string;
    profiles: SocialProfile[];
    latest_analysis?: InfluencerAnalysis;
    collaboration_count: number;
}

export interface MarketingCampaign {
    id: number;
    name: string;
    status: 'planning' | 'active' | 'completed';
    status_display: string;
    budget: number;
    start_date?: string;
    end_date?: string;
    description: string;
    stats: {
        influencer_count: number;
        total_spent: number;
        total_sales: number;
        roi: number;
    };
}

export interface Collaboration {
    id: number;
    campaign: number;
    campaign_name: string;
    influencer: number;
    influencer_name: string;
    status: 'contacting' | 'sample_sent' | 'content_live' | 'paid' | 'completed' | 'cancelled';
    status_display: string;
    cost_fee: number;
    cost_product: number;
    result_views: number;
    result_sales: number;
    result_clicks: number;
    total_cost: number;
    roi: number;
    notes: string;
    updated_at: string;
}

export interface InteractionLog {
    id: number;
    influencer: number;
    type: 'email' | 'dm' | 'contract' | 'payment' | 'sample' | 'other';
    type_display: string;
    content: string;
    date: string;
    user: number;
    user_name: string;
}

// ============ API Service ============

export const marketingAPI = {
    // Influencers
    listInfluencers: (params?: any) => api.get<Influencer[]>('/marketing/influencers/', { params }),
    getInfluencer: (id: number) => api.get<Influencer>(`/marketing/influencers/${id}/`),
    createInfluencer: (data: Partial<Influencer>) => api.post<Influencer>('/marketing/influencers/', data),
    updateInfluencer: (id: number, data: Partial<Influencer>) => api.patch<Influencer>(`/marketing/influencers/${id}/`, data),
    deleteInfluencer: (id: number) => api.delete(`/marketing/influencers/${id}/`),
    analyzeInfluencer: (id: number) => api.post(`/marketing/influencers/${id}/analyze/`),

    // Campaigns
    listCampaigns: (params?: any) => api.get<MarketingCampaign[]>('/marketing/campaigns/', { params }),
    getCampaign: (id: number) => api.get<MarketingCampaign>(`/marketing/campaigns/${id}/`),
    createCampaign: (data: Partial<MarketingCampaign>) => api.post<MarketingCampaign>('/marketing/campaigns/', data),
    updateCampaign: (id: number, data: Partial<MarketingCampaign>) => api.patch<MarketingCampaign>(`/marketing/campaigns/${id}/`, data),
    deleteCampaign: (id: number) => api.delete(`/marketing/campaigns/${id}/`),

    // Collaborations
    listCollaborations: (params?: any) => api.get<Collaboration[]>('/marketing/collaborations/', { params }),
    createCollaboration: (data: Partial<Collaboration>) => api.post<Collaboration>('/marketing/collaborations/', data),
    updateCollaboration: (id: number, data: Partial<Collaboration>) => api.patch<Collaboration>(`/marketing/collaborations/${id}/`, data),
    getCollaborationsByInfluencer: (influencerId: number) => api.get<Collaboration[]>('/marketing/collaborations/by_influencer/', { params: { influencer_id: influencerId } }),

    // Interactions
    listInteractions: (params?: any) => api.get<InteractionLog[]>('/marketing/interactions/', { params }),
    createInteraction: (data: Partial<InteractionLog>) => api.post<InteractionLog>('/marketing/interactions/', data),
};
